import React, { useEffect, useState } from "react";
import { Popper } from "@mui/material";

// Finds the element marked data-guide="<name>". Polls because targets mount late
// (the Schedule Interview button only appears after a resume is parsed).
export const useElement = (selector) => {
  const [el, setEl] = useState(null);
  useEffect(() => {
    if (!selector) {
      setEl(null);
      return undefined;
    }
    const find = () => {
      const next = document.querySelector(selector);
      setEl((prev) => (prev === next ? prev : next));
    };
    find();
    const id = setInterval(find, 300);
    return () => clearInterval(id);
  }, [selector]);
  return el;
};

// Every element matching the selector (e.g. a group of filter buttons). The same
// array is returned until the set of elements actually changes.
const NONE = [];
export const useElements = (selector) => {
  const [els, setEls] = useState(NONE);
  useEffect(() => {
    if (!selector) {
      setEls(NONE);
      return undefined;
    }
    const find = () => {
      const next = Array.from(document.querySelectorAll(selector));
      setEls((prev) =>
        prev.length === next.length && prev.every((el, i) => el === next[i]) ? prev : next.length ? next : NONE
      );
    };
    find();
    const id = setInterval(find, 300);
    return () => clearInterval(id);
  }, [selector]);
  return els;
};

const useGuideTarget = (name) => useElement(`[data-guide="${name}"]`);

// The element's on-screen box, kept current every frame (it moves as the page
// scrolls or reflows).
const useRect = (el) => {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!el) {
      setRect(null);
      return undefined;
    }
    let frame;
    const track = () => {
      const r = el.getBoundingClientRect();
      setRect((prev) =>
        prev && prev.top === r.top && prev.left === r.left && prev.width === r.width && prev.height === r.height
          ? prev
          : { top: r.top, left: r.left, width: r.width, height: r.height },
      );
      frame = requestAnimationFrame(track);
    };
    track();
    return () => cancelAnimationFrame(frame);
  }, [el]);
  return rect;
};

/**
 * While the guide is asking for one specific action, nothing else on the page
 * can be clicked: a transparent layer covers the screen, with a hole cut out
 * over each target so the step itself still works. The guide's own card or
 * pop-over sits above this layer (higher z-index) and stays usable.
 */
export const ClickShield = ({ holes = [], pad = 6, zIndex = 1299 }) => {
  const [size, setSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const onResize = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  let d = `M0 0 H${size.w} V${size.h} H0 Z`;
  holes.filter(Boolean).forEach((r) => {
    const w = r.width + pad * 2;
    const h = r.height + pad * 2;
    d += ` M${r.left - pad} ${r.top - pad} h${w} v${h} h${-w} Z`;
  });

  return (
    <div
      aria-hidden="true"
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        inset: 0,
        zIndex,
        background: "transparent",
        // even-odd: the screen minus the holes. Clipped-out areas don't take
        // clicks, so they reach the target underneath.
        clipPath: `path(evenodd, "${d}")`,
      }}
    />
  );
};

// Ring around the target. pointer-events: none so clicks reach the target.
const Spotlight = ({ el }) => {
  const rect = useRect(el);
  if (!rect) return null;
  const pad = 6;
  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        top: rect.top - pad,
        left: rect.left - pad,
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
        borderRadius: 10,
        boxShadow: "0 0 0 3px #ec4899",
        pointerEvents: "none",
        zIndex: 1300,
        transition: "all 0.15s ease-out",
      }}
    />
  );
};

const arrowSx = {
  '&[data-popper-placement^="bottom"] .gh-guide-arrow': { top: -6 },
  '&[data-popper-placement^="top"] .gh-guide-arrow': { bottom: -6 },
  '&[data-popper-placement^="right"] .gh-guide-arrow': { left: -6 },
  '&[data-popper-placement^="left"] .gh-guide-arrow': { right: -6 },
};

const CoachMark = ({
  target,
  title,
  body,
  progress,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
  onSkip,
  placement = "bottom",
  // Only the pink ring around the target, no popover
  ringOnly = false,
  // false: the rest of the page stays clickable while this mark is shown
  blockPage = true,
}) => {
  const el = useGuideTarget(target);
  const rect = useRect(el);
  const [arrowEl, setArrowEl] = useState(null);

  useEffect(() => {
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [el]);

  if (!el) return null;
  if (ringOnly) return <Spotlight el={el} />;

  return (
    <>
      {/* Only the highlighted step and this pop-over can be clicked */}
      {blockPage && <ClickShield holes={[rect]} />}
      <Spotlight el={el} />
      <Popper
        open
        anchorEl={el}
        placement={placement}
        sx={{ zIndex: 1301, ...arrowSx }}
        modifiers={[
          { name: "offset", options: { offset: [0, 16] } },
          { name: "preventOverflow", options: { padding: 12 } },
          { name: "arrow", enabled: !!arrowEl, options: { element: arrowEl } },
        ]}
      >
        <div
          role="dialog"
          aria-label={title}
          className="relative w-[300px] rounded-xl bg-white dark:bg-[#1B1524] border border-gray-200 dark:border-[#3a2f4a] shadow-2xl p-4 flex flex-col gap-2"
        >
          <span
            ref={setArrowEl}
            className="gh-guide-arrow absolute w-3 h-3 rotate-45 bg-white dark:bg-[#1B1524]"
          />
          {progress && (
            <span className="text-[11px] font-medium uppercase tracking-wider text-pink-500">{progress}</span>
          )}
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-300">{body}</p>
          <div className="flex items-center justify-between gap-2 mt-2">
            <button
              type="button"
              onClick={onSkip}
              className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
            >
              Skip guide
            </button>
            <div className="flex items-center gap-2">
              {secondaryLabel && (
                <button
                  type="button"
                  onClick={onSecondary}
                  className="text-sm px-3 py-1.5 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10"
                >
                  {secondaryLabel}
                </button>
              )}
              <button
                type="button"
                onClick={onPrimary}
                className="text-sm font-semibold px-3 py-1.5 rounded-lg text-white bg-gradient-to-r from-pink-500 to-blue-500"
              >
                {primaryLabel}
              </button>
            </div>
          </div>
        </div>
      </Popper>
    </>
  );
};

export default CoachMark;
